import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Container, Button, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Alert, TextField } from '@mui/material';

import { IMaterial } from "../../../types/material";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import { MaterialService } from "../../../services/materialService";

import MaterialTable from "../../tables/materialTable";
import Navigation from "../../../components/header";
import PermissionGate from "../../../components/permissionGate";
import Spinner from "../../../components/spinner";
import Json from "../../json";

import "../../../styles/entity/materialList.scss";

const MaterialList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<IMaterial[]>();

	//Filtering
	const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
	const [idFilter, setIdFilter] = useState<number>(0);
	const [materialFilter, setMaterialFilter] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("All");
	const [hazardousFilter, setHazardousFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("true");

	const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	const loadMaterials = async () => {
		setIsLoadingMaterials(true);
		setErrorMessage(undefined);

		MaterialService.getMaterials()
			.then((response) => {
				console.log('MaterialService.getMaterials: SUCCESS', response);

				let categories: string[] = [];

				let materials = MaterialService.mapMaterials(response.data);

				materials.map((item) => {
					if (item.category && !categories.includes(item.category)) {
						categories.push(item.category);
					}
				});

				setGridView(materials);
				setCategoryOptions(categories.sort());
			})
			.catch((error) => {
				console.log('MaterialService.getMaterials: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setIsLoadingMaterials(false);
			});
	}

	useEffect(() => {
		loadMaterials();
	}, [])

	const handleSelectCategory = (event: SelectChangeEvent) => {
		setCategoryFilter(event.target.value);
	};

	const handleSelectHazardous = (event: SelectChangeEvent) => {
		setHazardousFilter(event.target.value);
	};

	const handleSelectStatus = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value);
	};

	const handleIdChanged = (id?: number) => {
		if (isNaN(id) || id < 0) return;
		setIdFilter(id);
	};

	const handleStatusChanged = (updatedMaterial: IMaterial) => {
		setGridView((prev) => {
			if (!prev) return prev;
			return prev.map((item) => item.id === updatedMaterial.id ? updatedMaterial : item);
		});
	};

	return (
		<div className="hwi-material-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageMaterials}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Search Materials</div>
							<div className="links">
								<NavLink to="/material/add"><Button variant="outlined">New Material</Button></NavLink>
							</div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<TextField 
									label="ID" 
									name="id" 
									value={idFilter == 0 ? '' : idFilter} 
									size="small" 
									onChange={(event) => { handleIdChanged(+event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<TextField 
									label="Material" 
									name="material" 
									value={materialFilter} 
									size="small" 
									onChange={(event) => { setMaterialFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Category</InputLabel>
								<Select
									value={categoryFilter}
									defaultValue="All"
									label="Category"
									onChange={handleSelectCategory}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									<MenuItem value="(Blank)" key="-2">(Blank)</MenuItem>
									{categoryOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Hazardous</InputLabel>
								<Select
									value={hazardousFilter}
									label="Hazardous"
									onChange={handleSelectHazardous}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									<MenuItem value="true" key="1">Hazardous</MenuItem>
									<MenuItem value="false" key="0">Non-Hazardous</MenuItem>
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={handleSelectStatus}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									<MenuItem value="true" key="1">Active</MenuItem>
									<MenuItem value="false" key="0">Inactive</MenuItem>
								</Select>
							</FormControl>
						</section>
					</section>
					<section className="results">
						<Spinner dependency={isLoadingMaterials} text="Loading materials...">
							<section className="results">
								{errorMessage &&
									<Alert className="alert-error"
										severity="error">
										{errorMessage}
									</Alert>
								}
								{!errorMessage &&
									<MaterialTable 
										data={gridView} 
										id={idFilter} 
										material={materialFilter} 
										category={categoryFilter} 
										hazardous={hazardousFilter} 
										status={statusFilter} 
										onStatusChanged={handleStatusChanged}
									></MaterialTable>
								}
							</section>
						</Spinner>
					</section>
				</PermissionGate>
			</Container>
			{/* <Json data={statusOptions}></Json> */}
		</div>
	);
};

export default MaterialList;